<script context="module" lang="ts">
    import {get, writable} from 'svelte/store'
    import TooltipComp from './tooltip-word-link.svelte';
    import type {UserWordsMapStore} from '$lib/classes/stores/get-user-words-map';
    import type {Translation, WordId} from '$lib/classes/data/_primitives';
    import type {Tooltip} from '$lib/classes/_types/tooltip';
    import {createClassComponent} from 'svelte/legacy';
    import type {UserActionProcessor} from '$lib/classes/services/user-action-processor';
    import type {BrowseNavigator} from '$lib/classes/services/browse-navigator';
    import {keyBrowseNavigator, keyUserActionProcessor} from '$lib/context-keys';
    import {goto} from '$app/navigation';

    // QUESTION why via module?!?
    // TODO rethink if this is the best way to handle tooltip !!!
    //  maybe possible via an action to extract logic from component
    //  or maybe better somehow with a 'real' component..?
    //  but then in sentence not here because it shouldn't 'sit' between word link spans..?!

    let uWM: UserWordsMapStore // userWordsMap

    const tooltip: Tooltip = writable({
        show: false,
        hovered: false,
        id: '',
        translation: '',
        anchor: document.body,
    })

    let tooltipComp: TooltipComp | null = null
    let timeout: ReturnType<typeof setTimeout>

    function createTooltip(id: WordId, translation: Translation, anchor: HTMLElement, userActionProcessor: UserActionProcessor, browseNavigator: BrowseNavigator) {
        if (tooltipComp) {
            tooltipComp.$destroy()
            tooltipComp = null
        }
        clearTimeout(timeout)

        tooltip.set({
            show: true,
            hovered: false,
            id,
            translation,
            anchor,
        })

        tooltipComp = createClassComponent({
            component: TooltipComp,
            target: anchor.closest('.parts-and-translation') ??
                anchor.closest('#sentence-and-translation') ??
                anchor.closest('.sentence-mobile') ??
                document.body,
            props: {
                userWordsMap: uWM,
                tooltip,
                // browseNavigator not available in '/learn' (modal)
                onNavigate: (wordId: WordId) => browseNavigator ? browseNavigator.navigateViaWordId(wordId) : goto(`/browse/${get(tooltip).id}`)
            },
            context: new Map([
                [keyUserActionProcessor, userActionProcessor]
            ]),
        })
    }

    function hideTooltip() {
        // NOTE timeout needed for when cursor switches from word to tooltip
        //  so that setting show:false after hovered:true (seemed to be fixed with Svelte5)
        timeout = setTimeout(() => {
            tooltip.update(t => {
                return {
                    ...t,
                    show: false
                }
            })
        }, 0)
    }

    tooltip.subscribe(t => {
        if (t.show === false && t.hovered === false) {
            if (tooltipComp) {
                tooltipComp.$destroy()
                tooltipComp = null
            }
        }
    })
</script>

<!--suppress JSUnusedAssignment, CssUnresolvedCustomProperty -->
<script lang="ts">
    import {getGenderClass} from '$lib/utils/getGenderClass';
    import type {WordLink} from '$lib/classes/data/sentence';
    import {getContext} from 'svelte';

    export let userWordsMap: UserWordsMapStore
    export let part: WordLink
    export let currentWordId: WordId
    export let colorCodeNouns: boolean = false

    const userActionProcessor: UserActionProcessor = getContext(keyUserActionProcessor)
    const browseNavigator: BrowseNavigator = getContext(keyBrowseNavigator)

    // set STORES in script context="module" to pass to tooltip component
    uWM = userWordsMap

    let span: HTMLSpanElement

    let id = part.id!
    let translation = part.t!

    let genderClass = colorCodeNouns ? getGenderClass(id) : ''

</script>

<!-- svelte-ignore a11y-click-events-have-key-events  a11y-no-static-element-interactions -->
<span bind:this={span}
      class="sentence-part {genderClass}"
      class:current-word={id === currentWordId}
      class:tooltip-is-visible={($tooltip.show || $tooltip.hovered) && $tooltip.anchor === span}
      on:click|stopPropagation
      on:touchstart|stopPropagation
      on:mouseenter|stopPropagation={() => createTooltip(id, translation, span, userActionProcessor, browseNavigator)}
      on:mouseleave={hideTooltip}
>
    {part.word}
</span>

<style>
    span {
        user-select: none;
    }

    .current-word {
        color: var(--color-current-word);
        font-weight: var(--font-bold)
    }

    .tooltip-is-visible {
        border-bottom: 3px solid var(--color-accent);
    }
</style>